import { makeAutoObservable, reaction, runInAction } from "mobx";
import { FIAT_CURRENCY_ENUM, ContractEntity, CONTRACT_STATUS_ENUM } from "interfaces";
import { getContractById, getContractStatusHistory, ContractHistoryRow } from "services";
import { NormalizedError } from "services/api/errorHandler";
import { statusFetching } from "services/authClient/constants/types";
import { buildContractStepsViewModel, ContractStepsViewModel } from "utils/contractHistoryPhase";

export type { ContractStepPayload, ContractStepsViewModel } from "utils/contractHistoryPhase";

export class ContractCardHistory {
  rows: ContractHistoryRow[] = [];
  loading = false;
  fetchError?: NormalizedError;

  constructor(private root: ContractCardStore) {
    makeAutoObservable(this);
    reaction(
      () => [this.root.card.id, this.root.card.status] as const,
      ([id]) => {
        if (id) {
          void this.fetchHistory(id);
        }
      }
    );
  }

  getStepsViewModel(): ContractStepsViewModel {
    return buildContractStepsViewModel(this.rows, this.root.card.status);
  }

  fetchHistory = async (id: string) => {
    this.loading = true;
    this.fetchError = undefined;

    try {
      const rows = await getContractStatusHistory({ id });
      runInAction(() => {
        this.rows = rows;
      });
    } catch (error: any) {
      runInAction(() => {
        this.fetchError = error;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };
}

export class ContractCardStore {
  public card: ContractEntity = {
    id: "",
    fiatAmount: 0,
    fiatCurrency: FIAT_CURRENCY_ENUM.USD,
    unk: "",
    companyName: "",
    companyRegistrationNumber: "",
    paidAmount: 0,
    date: "",
    direction: "EXP",
    created: "",
    counterpartyCountry: "",
    counterpartyCountryCode: "",
    counterpartyName: "",
    status: CONTRACT_STATUS_ENUM.DRAFT,
    statusDate: "",
    documents: {},
    companyId: "",
    counterpartyActualAddress: "",
    counterpartyLegalAddress: "",
    bankName: "",
    bankBik: "",
    foreignOrganizationCode: "",
  };
  public fetchStatus: statusFetching = "init";
  public fetchError?: NormalizedError;

  readonly history: ContractCardHistory;

  constructor() {
    makeAutoObservable(this);
    this.history = new ContractCardHistory(this);
  }

  get isPending() {
    return this.fetchStatus === "fetching";
  }

  fetchCardById = async (id: string) => {
    runInAction(() => {
      this.fetchStatus = "fetching";
      this.fetchError = undefined;
    });

    return getContractById({ id })
      .then((res) => {
        runInAction(() => {
          this.card = res;
          this.fetchStatus = "success";
        });
      })
      .catch((error: NormalizedError) => {
        runInAction(() => {
          this.fetchStatus = "error";
          this.fetchError = error;
        });
      });
  };

  updateCardById = async (id: string) => {
    return getContractById({ id })
      .then((res) => {
        runInAction(() => {
          this.card = res;
        });
      })
      .catch((error: NormalizedError) => {
        runInAction(() => {
          this.fetchStatus = "error";
          this.fetchError = error;
        });
      });
  };
}
